<template>

  <div>

    <div class="google-asset-map-container" :style="{'height': map_h, 'width': '100%'}">

      <!-- Google map loads here... -->
      <div ref="gmap" :style="{'height': map_h, 'width': '100%'}"></div>

    </div>

  </div>

</template>

<style scoped>

  .google-asset-map-container {
    background: url('https://cdnjs.cloudflare.com/ajax/libs/file-uploader/3.7.0/processing.gif') center center no-repeat;
  }

</style>

<script>

import _ from 'lodash';

export default {

  props: ['assetLatitude', 'assetLongitude', 'centerLatitude', 'centerLongitude', 'mapHeight'],

  data() {

    return {

      asset_lng: false,
      asset_lng: false,
      center_lat: false,
      center_lng: false,
      map_h: '500px',
    };
  },

  watch: {

    // @note - We setup custom watchers for center and asset coordinates during mounted lifecycle
    // (the watchers are more complicated and require watching a combination of 2+ properties)
  },

  mounted() {

    const vm = this;

    // Set sensible defaults if not available from props
    const asset_lat = (this.assetLatitude && this.assetLongitude) ? this.assetLatitude : false;
    const asset_lng = (this.assetLatitude && this.assetLongitude) ? this.assetLongitude : false;
    const center_lat = (this.centerLatitude && this.centerLongitude) ? this.centerLatitude : -36.848285;
    const center_lng = (this.centerLatitude && this.centerLongitude) ? this.centerLongitude : 174.762202;

    // Center the map on the asset if possible...
    this.$data.asset_lat = asset_lat;
    this.$data.asset_lng = asset_lng;
    this.$data.center_lat = asset_lat || center_lat;
    this.$data.center_lng = asset_lng || center_lng;
    this.$data.map_h = this.mapHeight || '500px';

    if ( ! window.google || ! window.google.load || ! process.env.GOOGLE_API_KEY) {

      this.$buefy.dialog.alert({
        title: 'Error',
        message: 'Google API loader and key is not correctly configured.',
        type: 'is-danger',
      });
    }
    else {

      // google.load('maps', '3', {other_params:'libraries=places'});
      google.load('maps', '3', {callback: this.onGoogleLoaded, other_params:`key=${process.env.GOOGLE_API_KEY}`});
    }

    // ---------------------------------------------------------------
    // START We need to be able to react to groups of props changing
    // https://github.com/vuejs/vue/issues/844#issuecomment-335026928
    // ---------------------------------------------------------------

    // When upstream ASSET coordinate props change, update the local values
    this.$watch(
      function(vm) {

        return [vm.assetLatitude, vm.assetLongitude].join(',');
      },
      _.debounce(function(csv) {

        const coordinates = vm.parseCoordinatesFromString(csv);
        const { lat, lng } = coordinates;

        vm.$data.asset_lat = lat;
        vm.$data.asset_lng = lng;

      }, 500)
    );

    // When upstream CENTER coordinate props change, update the local values
    this.$watch(
      function(vm) {

        return [vm.centerLatitude, vm.centerLongitude].join(',');
      },
      _.debounce(function(csv) {

        const coordinates = vm.parseCoordinatesFromString(csv);
        const { lat, lng } = coordinates;

        vm.$data.center_lat = lat;
        vm.$data.center_lng = lng;

      }, 500)
    );

    // When $data ASSET coordinates change, update Google Map.
    this.$watch(
      function(vm) {

        return [vm.$data.asset_lat, vm.$data.asset_lng].join(',');
      },
      _.debounce(function(csv) {

        const coordinates = vm.parseCoordinatesFromString(csv);
        const { lat, lng } = coordinates;

        vm.setAssetMarker(lat, lng);

      }, 500)
    );

    // When $data CENTER coordinate props change, update Google Map.
    this.$watch(
      function(vm) {

        return [vm.$data.center_lat, vm.$data.center_lng].join(',');
      },
      _.debounce(function(csv) {

        const coordinates = vm.parseCoordinatesFromString(csv);
        const { lat, lng } = coordinates;

        if (lat && lng) {

          vm.setMapCenter(lat, lng);
        }
      }, 500)
    );

    // ---------------------------------------------------------------
    // END We need to be able to react to groups of props changing
    // ---------------------------------------------------------------
  },

  beforeDestroy() {

    // ----------------------------------------------------
    // START Clean up event listeners and intervals etc.
    // ----------------------------------------------------

    // Clear up the google map event listeners
    if (this.gmap !== undefined) {

      google.maps.event.clearListeners(this.gmap, 'click');
    }

    // ----------------------------------------------------
    // END Clean up event listeners and intervals etc.
    // ----------------------------------------------------
  },

  methods: {

    onGoogleLoaded() {

      const vm = this;

      // We're using nextTick to help ensure that vm.$refs are ready...
      vm.$nextTick(() => {

        const mapDiv = vm.$refs.gmap;
        let latLng = new google.maps.LatLng(vm.$data.center_lat, vm.$data.center_lng);

        let mapOptions = {
          zoom: 15,
          center: latLng,
          // mapTypeId: google.maps.MapTypeId.SATELLITE
          mapTypeId: google.maps.MapTypeId.HYBRID,
          tilt: 0,
          disableDefaultUI: true,
          zoomControl: true,
          styles: [
            {featureType: "poi", stylers: [{ visibility: "off" }]},
            {featureType: "transit", elementType: "labels.icon", stylers: [{visibility: "off"}]}
          ]
        };

        // @reminder - you can programatically control most things
        // e.g. apply certain styles for showing bus stops etc.
        // https://developers.google.com/maps/documentation/javascript/examples/hiding-features
        // vm.gmap.setOptions({styles: styles['default']});

        // We add the map variable to this Vue instance, but NOT in a reactive way
        // (a Google map obj is too complex to be stored in $data as observable)
        vm.gmap = new google.maps.Map(mapDiv, mapOptions);

        // Disable the 45 degree tilt for hybrid and satellite map types
        // (realized that you can actually spec in mapOptions object)
        // vm.gmap.setTilt(0);

        // Manually set a map marker for the initial asset if we have one on load...
        if (vm.$data.asset_lat && vm.$data.asset_lng) {

          vm.setAssetMarker(vm.$data.asset_lat, vm.$data.asset_lng);
        }

        // @reminder - always clear event listeners in beforeDestroy
        google.maps.event.addListener(vm.gmap, 'click', function(event) {

          const coordinates = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          };

          vm.setAssetMarker(coordinates.lat, coordinates.lng);
          vm.$emit('coordinates', coordinates);
        });
      });
    },

    parseCoordinatesFromString(csv) {

      let parts = csv.split(',');
      let lat = parts[0] ? _.trim(parts[0]) : false;
      let lng = parts[1] ? _.trim(parts[1]) : false;

      lat = lat ? parseFloat(lat) : lat;
      lat = _.isFinite(lat) ? lat : false;
      lng = lng ? parseFloat(lng) : lng;
      lng = _.isFinite(lng) ? lng : false;

      return {lat, lng};
    },

    setMapCenter: function(latitude, longitude) {

      // Don't continue if we don't even have a map object yet...
      if ( ! this.gmap) return;

      const coordinates = (latitude && longitude) ? {lat: latitude, lng: longitude} : false;

      // Don't change the center if we have an asset marker data available
      if (coordinates && ! this.$data.asset_lat && ! this.$data.asset_lng) {

        // this.gmap.setCenter({lat: this.$data.center_lat, lng: this.$data.center_lng});
        this.gmap.panTo(coordinates);
      }
    },

    setAssetMarker: function(latitude, longitude) {

      // Don't continue if we don't even have a map object yet...
      if ( ! this.gmap) return;

      const coordinates = (latitude && longitude) ? {lat: parseFloat(latitude), lng: parseFloat(longitude)} : false;

      if (coordinates) {

        if ( ! this.gmapmarker) {

          this.gmapmarker = new google.maps.Marker({
            position: coordinates,
            map: this.gmap
          });
        }
        else {
          this.gmapmarker.setPosition(coordinates);
          this.gmapmarker.setMap(this.gmap);
        }
      }
      else {

        if (this.gmapmarker) this.gmapmarker.setMap(null);
      }
    },
  }
};

</script>


// WEBPACK FOOTER //
// src/components/GoogleAssetMap.vue